import type { RootState } from "../../store";
import { padAff } from "./affPadding";

/**
 * Returns business context header values from Redux state,
 * or null if the app hasn't completed initial load yet.
 */
export function getBusinessContextHeaders(state: RootState): { business: string; period: string } | null {
  const shellConfig = state.boot.shellConfig;
  if (!shellConfig) {
    return null;
  }

  const user = state.boot.user;
  if (!user) {
    return null;
  }

  const { aff, abo } = user;
  const selectedPeriod = state.period?.selectedPeriod;

  if (!aff || !abo || !selectedPeriod) {
    return null;
  }

  // Ensure aff is padded to 3 digits
  const paddedAff = padAff(aff);

  return {
    business: `${paddedAff}-${abo}`,
    period: selectedPeriod,
  };
}

/**
 * Applies business context headers to a Headers object if available.
 * Use this in RTK Query prepareHeaders callbacks to avoid duplicating
 * the header-name and conditional logic across multiple API slices.
 */
export function applyBusinessContextHeaders(headers: Headers, state: RootState): void {
  const businessHeaders = getBusinessContextHeaders(state);
  if (businessHeaders) {
    headers.set("x-amway-selected-business", businessHeaders.business);
    headers.set("x-amway-selected-period", businessHeaders.period);
  }
}

/**
 * RTK Query endpoint names (not URL paths) that should never receive business context headers.
 * All endpoints hitting /gql/los/graphql are excluded because that API does not use these headers.
 */
export const EXCLUDED_ENDPOINTS: ReadonlySet<string> = new Set([
  "getBusinessSelector",
  "getProfile",
  "getContactInfo",
  "getSponsorInfo",
  "getOverViewCardsDetails",
  "getNote",
  "createNote",
  "updateNote",
  "deleteNote",
]);
